import { BaseWebComponent } from "./base-components/base-web-component.js";
import { FullSearchComponent } from "./full-search-component.js";
import type { ColumnData, ListFilter } from "../types/ListManagerTypes.js";
import type { BootstrapOffcanvas } from "../types/bootstrap.js";
import {  templateParseToElement } from "../logic/templateParse.js";
import ListFilters from "../logic/ListFilters.js";

/**
 * Full Search Panel Web Component - Offcanvas Filter System
 * 
 * Uses FullSearchComponent for complete filter management:
 * - Modern web components architecture
 * - Direct integration with individual filter components
 * - Auto-apply filters (no manual apply button needed)
 * - Bootstrap Offcanvas container
 * 
 * Maintains API compatibility with FullSearchPanelController.
 */
export class FullSearchPanel extends BaseWebComponent {
    static webComponentName = "full-search-panel";
    
    // =============================
    // Panel Configuration (integrated from PanelElementBase)
    // =============================
    
    /** Panel-specific configuration defaults */
    protected panelDefaults: Record<string, any> = {
        "visible": true,
        "collapsible": false,
        "expanded": true
    };
    
    /** Panel state management */
    protected _isInitialized = false;
    protected _isVisible = true;
    
    // =============================
    // Configuration Properties
    // =============================
    
    /** ID of the target ListManager container */
    private targetListId = "";
    
    /** Available fields for filtering */
    private availableFields: ColumnData[] = [];
    
    /** Maximum number of filters allowed */
    private maxFilters = 10;
    
    // Note: debug property is inherited from BaseWebComponent (protected)
    
    // =============================
    // Internal State
    // =============================
    
    /** Bootstrap Offcanvas instance */
    private offcanvasInstance: BootstrapOffcanvas | null = null;
    
    /** Offcanvas container element */
    private offcanvasElement: HTMLElement | null = null;
    
    // =============================
    // FullSearchComponent Integration
    // =============================
    
    /** FullSearchComponent instance for filter management */
    private fullSearchComponent: FullSearchComponent | null = null;
    
    /** ListFilters instance for component configuration */
    private listFilters: ListFilters | null = null;
    
    // =============================
    // Public API - Required by FullSearchPanelController
    // =============================
    
    /** Get current view state (required by FullSearchPanelController) */
    public get viewState(): ('open' | 'closed') {
        return this.offcanvasElement && this.offcanvasElement.classList.contains('show') ? 'open' : 'closed';
    }
    
    /**
     * Show the offcanvas panel (required by FullSearchPanelController)
     */
    public show(): void {
        if (this.offcanvasInstance) {
            this.offcanvasInstance.show();
        }
        else {
            console.error("Offcanvas instance not initialized");
        }
    }
    
    /**
     * Hide the offcanvas panel (required by FullSearchPanelController)
     */
    public hide(): void {
        if (this.offcanvasInstance) {
            this.offcanvasInstance.hide();
        }
    }
    
    /**
     * Toggle the offcanvas panel (required by FullSearchPanelController)
     */
    public toggle(): void {
        if (this.offcanvasInstance) {
            this.offcanvasInstance.toggle();
        }
    }
    
    /**
     * Set available fields for filtering (required by FullSearchPanelController)
     */
    public setAvailableFields(fields: ColumnData[]): void {
        this.availableFields = fields;
        
        if (this.fullSearchComponent && this.listFilters) {
            // Configure FullSearchComponent with updated fields
            this.fullSearchComponent.listFilters = this.listFilters;
        }
        
        if (this.debug) {
            console.log('FullSearchPanel: Available fields updated:', fields.length);
        }
    }
    
    /**
     * Get all active filters as ListFilter objects (required by FullSearchPanelController)
     */
    public getAllFilters(): ListFilter[] {
        if (this.fullSearchComponent) {
            return this.fullSearchComponent.collectFiltersFromComponents();
        }
        return [];
    }
    
    // =============================
    // Panel Configuration Methods (integrated from PanelElementBase)
    // =============================
    
    /**
     * Build rendering data with panel-specific defaults
     * Extends base buildRenderingData with panel-specific data
     */
    protected buildRenderingData(): Record<string, any> {
        // Start with base data from parent
        const data = super.buildRenderingData();
        
        // Add panel-specific defaults (lowest priority)
        Object.keys(this.panelDefaults).forEach(key => {
            if (!(key in data)) {
                data[key] = this.panelDefaults[key];
            }
        });
        
        // Add panel state
        data.isInitialized = this._isInitialized;
        data.isVisible = this._isVisible;
        
        return data;
    }
    
    /**
     * Get configuration from attributes with panel-specific defaults
     */
    protected getPanelConfig<T = any>(key: string, defaultValue?: T): T {
        // Try data-options first
        const options = this.parseDataOptions();
        if (key in options) {
            return options[key] as T;
        }
        
        // Try direct attribute
        const attr = this.getAttribute(key);
        if (attr !== null) {
            // Try to parse as JSON for complex types
            try {
                return JSON.parse(attr) as T;
            } catch {
                return attr as T;
            }
        }
        
        // Try panel defaults
        if (key in this.panelDefaults) {
            return this.panelDefaults[key] as T;
        }
        
        // Use provided default or undefined
        return defaultValue as T;
    }
    
    /**
     * Check if panel is initialized
     */
    get isInitialized(): boolean {
        return this._isInitialized;
    }
    
    /**
     * Force re-initialization (useful for dynamic updates)
     */
    reinitialize(): void {
        this._isInitialized = false;
        this.render();
    }
    
    // =============================
    // Lifecycle & Setup
    // =============================
    
    constructor() {
        super();
        // FullSearchPanel doesn't use templateId - it has custom rendering logic
        
        // Parse initial visibility state (from PanelElementBase onConnected)
        this._isVisible = !this.hasAttribute('hidden') && 
                         this.getAttribute('visible') !== 'false' &&
                         !this.classList.contains('d-none');
    }
    
    /**
     * Override render() since FullSearchPanel doesn't use template system
     * It has its own custom rendering logic (renderOffcanvas)
     */
    protected render(): void {
        // FullSearchPanel doesn't use external templates
        // Call afterRender directly to trigger initialization
        this.afterRender();
    }
    
    /**
     * Called after successful render (integrated from PanelElementBase afterRender)
     */
    protected afterRender(): void {
        if (!this._isInitialized) {
            this.initializePanel();
            this._isInitialized = true;
        }
        this.setupEventListeners();
    }
    
    /**
     * Initialize panel-specific functionality (offcanvas-specific setup)
     */
    protected initializePanel(): void {
        console.log("Initializing FullSearchPanel");
        // Parse configuration from attributes
        this.parseConfiguration();
        
        // Render the offcanvas structure
        this.renderOffcanvas();
        
        // Initialize Bootstrap Offcanvas
        this.initializeOffcanvas();
        
        // Initialize FullSearchComponent AFTER offcanvas is rendered
        this.initializeFullSearchComponent();
        
        this.debugLog('FullSearchPanel initialized:', {
            targetListId: this.targetListId,
            availableFields: this.availableFields.length,
            maxFilters: this.maxFilters
        });
    }
    
    /**
     * Cleanup panel-specific resources (integrated from PanelElementBase)
     */
    protected cleanupPanel(): void {
        // Cleanup Bootstrap instance
        if (this.offcanvasInstance) {
            this.offcanvasInstance.dispose();
            this.offcanvasInstance = null;
        }
        
        // Cleanup FullSearchComponent reference
        this.fullSearchComponent = null;
        this.listFilters = null;
    }
    
    /**
     * Override onDisconnected to cleanup panel resources (from PanelElementBase)
     */
    protected onDisconnected(): void {
        this.debugLog('Panel disconnected from DOM');
        this.cleanupPanel();
    }
    
    // =============================
    // Configuration Parsing
    // =============================
    
    private parseConfiguration(): void {
        this.targetListId = this.getAttribute('data-target-list') || '';
        this.maxFilters = parseInt(this.getAttribute('data-max-filters') || '10');
        // Note: debug is automatically parsed by BaseWebComponent constructor
        
        // Parse available fields from JSON attribute
        const fieldsAttr = this.getAttribute('data-available-fields');
        if (fieldsAttr) {
            try {
                this.availableFields = JSON.parse(fieldsAttr);
            } catch (error) {
                console.warn('Failed to parse data-available-fields:', error);
                this.availableFields = [];
            }
        }
        
        // If no fields provided, try to get from data-options
        if (this.availableFields.length === 0) {
            const options = this.parseDataOptions();
            if (options.availableFields) {
                this.availableFields = options.availableFields;
            }
        }
    }
    
    // =============================
    // Template System
    // =============================
    
    /**
     * Render offcanvas structure using template system
     */
    private renderOffcanvas(): void {
        const offcanvasId = `offcanvas-${this.targetListId || 'search'}`;
        
        const templateData = {
            offcanvasId,
            filterCount: 0 // FullSearchComponent will manage its own count
        };
        templateParseToElement('full-search-panel-template', this, templateData);
        this.offcanvasElement = this.querySelector('.offcanvas');
    }
    
    // =============================
    // FullSearchComponent Integration
    // =============================
    
    /**
     * Initialize FullSearchComponent and setup integration
     */
    private initializeFullSearchComponent(): void {
        // Find the full-search-component element in the rendered template
        this.fullSearchComponent = this.querySelector('full-search-component') as FullSearchComponent;
        
        if (!this.fullSearchComponent) {
            console.error('FullSearchPanel: full-search-component not found in template');
            return;
        }
        
        // Setup event listener for filter changes
        this.fullSearchComponent.addEventListener('filters-change', this.handleFiltersChange.bind(this));
        
        if (this.debug) {
            console.log('FullSearchPanel: FullSearchComponent initialized and connected');
        }
    }
    
    /**
     * Set ListFilters instance for FullSearchComponent configuration
     */
    public setListFilters(listFilters: ListFilters): void {
        this.listFilters = listFilters;
        
        if (this.fullSearchComponent) {
            this.fullSearchComponent.listFilters = listFilters;
            
            if (this.debug) {
                console.log('FullSearchPanel: ListFilters configured on FullSearchComponent');
            }
        }
    }
    
    // =============================
    // Bootstrap Integration
    // =============================
    
    private initializeOffcanvas(): void {
        
        if (!this.offcanvasElement) {
            console.error("Offcanvas element not found");
            return;
        }
        console.log("Initializing Bootstrap Offcanvas for FullSearchPanel", this.offcanvasElement);
        try {
            this.offcanvasInstance = new window.bootstrap.Offcanvas(this.offcanvasElement, {
                backdrop: true,
                keyboard: true,
                scroll: false
            });
        } catch (error) {
            console.error('Failed to initialize Bootstrap Offcanvas:', error);
        }
    }
    

    // =============================
    // Event Listeners Setup
    // =============================
    
    protected setupEventListeners(): void {
        // Bootstrap offcanvas events (required by FullSearchPanelController)
        if (this.offcanvasElement) {
            this.offcanvasElement.addEventListener('show.bs.offcanvas', this.handleOffcanvasShow.bind(this));
            this.offcanvasElement.addEventListener('hide.bs.offcanvas', this.handleOffcanvasHide.bind(this));
        }
        
        // Note: FullSearchComponent handles its own filter-related events
        // We only listen to its filters-change event in initializeFullSearchComponent
    }
    
    // =============================
    // Event Handling
    // =============================
    
    /**
     * Handle filters change events from FullSearchComponent
     */
    private handleFiltersChange(event: Event): void {
        const customEvent = event as CustomEvent;
        const { filters } = customEvent.detail;
        
        // Auto-apply filters (dispatch filters-applied event for FullSearchPanelController)
        this.applyFilters(filters);
        
        if (this.debug) {
            console.log('FullSearchPanel: Filters changed from FullSearchComponent, count:', filters.length);
        }
    }
    
    /**
     * Apply current filters - dispatch filters-applied event (required by FullSearchPanelController)
     */
    private applyFilters(filters?: ListFilter[]): void {
        const filtersToApply = filters || this.getAllFilters();
        
        // Dispatch filters-applied event (required by FullSearchPanelController)
        const event = new CustomEvent('filters-applied', {
            detail: {
                filters: filtersToApply,
                targetListId: this.targetListId,
                component: this
            },
            bubbles: true
        });
        this.dispatchEvent(event);
        
        if (this.debug) {
            console.log('FullSearchPanel: Applied filters:', filtersToApply);
        }
    }
    
    // =============================
    // Event Handlers
    // =============================
    
    private handleOffcanvasShow(): void {
        // Dispatch panel-opened event (required by FullSearchPanelController)
        const customEvent = new CustomEvent('panel-opened', {
            detail: { component: this },
            bubbles: true
        });
        this.dispatchEvent(customEvent);
    }
    
    private handleOffcanvasHide(): void {
        // Dispatch panel-closed event (required by FullSearchPanelController)
        const customEvent = new CustomEvent('panel-closed', {
            detail: { component: this },
            bubbles: true
        });
        this.dispatchEvent(customEvent);
    }
    
    // =============================
    // Helper Methods
    // =============================
    
    private showMessage(text: string, type: 'success' | 'warning' | 'danger' = 'warning'): void {
        // Simple console message for now (can be enhanced with toast notifications)
        console.log(`[${type}] ${text}`);
    }
    
    // =============================
    // Debug Methods
    // =============================
    
    public getDebugInfo(): object {
        return {
            component: 'full-search-panel',
            config: {
                targetListId: this.targetListId,
                maxFilters: this.maxFilters,
                availableFields: this.availableFields.length
            },
            state: {
                viewState: this.viewState,
                filtersCount: this.getAllFilters().length
            },
            integration: {
                fullSearchComponent: !!this.fullSearchComponent,
                listFilters: !!this.listFilters
            },
            filters: this.getAllFilters()
        };
    }
}

// Component registration handled by index.ts